import { Navigate, Route, Routes } from 'react-router-dom';
import { useGlobalState } from './store';
import { getAuthState } from './utils/auth';

// Core pages
import HomePage from './components/core/pages/HomePage';
import LoginPage from './components/core/pages/LoginPage';
import LogoutPage from './components/core/pages/LogoutPage';
import ReferralPage from './components/core/pages/ReferralPage';
import SettingsPage from './components/core/pages/SettingsPage';
import SupportPage from './components/core/pages/SupportPage';
import TrialBlockPage from './components/core/pages/TrialBlockPage';

// Dashboard
import DashboardRoute from './components/routers/DashboardRoute';

// Accounts
import AccountCreatePage from './components/accounts/pages/AccountCreatePage';
import AccountListPage from './components/accounts/pages/AccountListPage';

// Syncs
import SyncCreateCompletePage from './components/syncs/pages/SyncCreateCompletePage';
import SyncCreatePage from './components/syncs/pages/SyncCreatePage';
import SyncCreatePageBatch from './components/syncs/pages/SyncCreatePageBatch';
import SyncDetailPage from './components/syncs/pages/SyncDetailPage';
import SyncEditPage from './components/syncs/pages/SyncEditPage';
import SyncListPage from './components/syncs/pages/SyncListPage';

// Schedules
import ScheduleEditPage from '@components/schedules/pages/ScheduleEditPage';
import ScheduleBookingAuthPage from './components/schedules/pages/ScheduleBookingAuthPage';
import ScheduleBookingCompletePage from './components/schedules/pages/ScheduleBookingCompletePage';
import ScheduleBookingPage from './components/schedules/pages/ScheduleBookingPage';
import ScheduleCreateCompletePage from './components/schedules/pages/ScheduleCreateCompletePage';
import ScheduleCreatePage from './components/schedules/pages/ScheduleCreatePage';
import ScheduleDetailPage from './components/schedules/pages/ScheduleDetailPage';
import ScheduleListPage from './components/schedules/pages/ScheduleListPage';


// Organization
import CreateOrganizationPage from './components/organization/pages/CreateOrganizationPage';
import OrganizationPage from './components/organization/pages/OrganizationPage';

// External
import Edit from './components/external/Edit';
import Start from './components/external/Start';

// OAuth
import GoogleOAuth from './pages/oauth/google';
import O365OAuth from './pages/oauth/o365';

// Public pages
import NotFoundPage from './pages/404';
import TermsAndPrivacyPage from './pages/terms-and-privacy';

// Admin
import AdminScanPage from './components/admin/pages/AdminScanPage';

// Integrations
import ZoomActivatePage from './components/integrations/pages/ZoomActivatePage';
import ZoomAuthPage from './components/integrations/pages/ZoomAuthPage';
import ComponentPlayground from './components/core/pages/ComponentPlayground'; // Added Playground

const PrivateRoute = ({ children }) => {
  const [user] = useGlobalState('user');
  const authState = getAuthState();
  
  // console.log('PrivateRoute - authState:', authState);
  // console.log('PrivateRoute - user:', user);
  
  // Check localStorage directly for authentication
  const isAuthenticated = localStorage.getItem('amplify-authenticator-authState') === 'signedIn';
  // console.log('PrivateRoute - isAuthenticated from localStorage:', isAuthenticated);
  
  if (!isAuthenticated) {
    // console.log('PrivateRoute - Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // If we're authenticated but don't have a user in global state yet, set it
  if (isAuthenticated && !user) {
    // console.log('PrivateRoute - Authenticated but no user in global state, setting up user');
    // We'll let the component mount and the DashboardRoute will handle setting the user
  }
  
  // console.log('PrivateRoute - Authenticated, rendering children');
  return children;
};

const App = () => {
  return (
    <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage isSignup={true} />} />
        <Route path="/terms-and-privacy" element={<TermsAndPrivacyPage />} />
        <Route path="/dev/playground" element={<ComponentPlayground />} /> {/* Added Playground Route */}
        
        {/* OAuth Routes */}
        <Route path="/oauth/google" element={<GoogleOAuth />} />
        <Route path="/oauth/o365" element={<O365OAuth />} />
        
        {/* Booking Routes */}
        <Route path="/book/auth" element={<ScheduleBookingAuthPage />} />
        <Route path="/book/complete" element={<ScheduleBookingCompletePage />} />
        <Route path="/book/:scheduleId" element={<ScheduleBookingPage />} />
        <Route path="/book/:scheduleId/:alias" element={<ScheduleBookingPage />} />
        {/* <Route path="/book/:scheduleId/:groupId/:key" element={<ScheduleBookingPage />} /> */}
        
        {/* External Routes */}
        <Route path="/external/start" element={<Start />} />
        <Route path="/external/edit" element={<Edit />} />
        
        {/* Dashboard Routes */}
        <Route path="/dashboard" element={
          <PrivateRoute>
            <DashboardRoute isAuthenticated={getAuthState() === 'signedIn'} />
          </PrivateRoute>
        }>
          {/* Add DashboardPage as the index route */}
          <Route index element={<HomePage />} />
          
          {/* Account Routes */}
          <Route path="accounts" element={<AccountListPage />} />
          <Route path="accounts/new" element={<AccountCreatePage />} />
          {/* <Route path="accounts/:id" element={<AccountDetailPage />} /> */}
          {/* <Route path="accounts/:accountId/edit" element={<AccountEditPage />} /> */}
          
          {/* Sync Routes */}
          <Route path="syncs" element={<SyncListPage />} />
          <Route path="syncs/new" element={<SyncCreatePage />} />
          <Route path="syncs/batch/new" element={<SyncCreatePageBatch />} />
          
          <Route path="syncs/complete" element={<SyncCreateCompletePage />} />
          <Route path="syncs/:syncId" element={<SyncDetailPage />} />
          <Route path="syncs/:syncId/edit" element={<SyncEditPage />} />
          
          {/* Schedule Routes */}
          <Route path="schedules" element={<ScheduleListPage />} />
          <Route path="schedules/new" element={<ScheduleCreatePage />} />
          <Route path="schedules/complete" element={<ScheduleCreateCompletePage />} />
          <Route path="schedules/:scheduleId" element={<ScheduleDetailPage />} />
          <Route path="schedules/:scheduleId/edit" element={<ScheduleEditPage />} />
          
        
          
          
           
          {/* Organization Routes */}
          <Route path="organization" element={<OrganizationPage />} />
          <Route path="organization/new" element={<CreateOrganizationPage />} />
          
          {/* Settings Routes */}
          <Route path="settings" element={<SettingsPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="referrals" element={<ReferralPage />} />
          <Route path="trial-block" element={<TrialBlockPage />} />
          <Route path="logout" element={<LogoutPage />} />
          
          {/* Admin Routes */}
          <Route path="admin/scan" element={<AdminScanPage />} />
          
          {/* Integration Routes */}
          <Route path="zoom" element={<ZoomAuthPage />} />
          <Route path="zoom/deauth" element={<ZoomActivatePage />} />
        </Route>
        
        {/* 404 Route */}
        <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default App;
